import { baseApi, apiTags } from "../baseApi";
import type {
    Service,
    ServiceListParams,
    UpdateServicePaidParams,
    AssignServicesParams,
    UpdateServiceArchiveParams,
    CalculateSuccessFeeParams,
    GenerateServiceExcelParams,
    ServicesOnConfirmationParams,
} from "../types/api.types";

export const servicesApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getServices: builder.query<Service[], ServiceListParams>({
            query: ({ clientId, isArchived }) => ({
                resource: "crm-services",
                action: "index",
                method: "GET",
                useAll: true, // Используем .all() для получения списка
                queryBuilder: (query: any) => {
                    // Сначала применяем фильтры, потом связи
                    if (isArchived !== null && isArchived !== undefined) {
                        query.where("is_archived", isArchived);
                    }

                    if (clientId) {
                        query.whereHas("clients", (clientsQuery: any) => {
                            clientsQuery.where("client_id", "=", clientId);
                        });
                        query.with("clients", (clientsQuery: any) => {
                            clientsQuery.orderBy("created_at").where("client_id", "=", clientId);
                        });
                    } else {
                        // Если clientId нет, все равно загружаем связи clients
                        query.with("clients");
                    }

                    return query;
                },
            }),
            providesTags: (result) =>
                result && Array.isArray(result)
                    ? [
                          { type: apiTags.Service, id: "LIST" },
                          ...result.map((item) => ({ type: apiTags.Service, id: item.id })),
                      ]
                    : [{ type: apiTags.Service, id: "LIST" }],
        }),
        updateServicePaid: builder.mutation<Service, UpdateServicePaidParams>({
            query: ({ ids, isPaid }) => ({
                resource: "crm-services",
                action: "paid",
                method: "PUT",
                id: ids[0],
                data: {
                    is_paid: isPaid ? 1 : 0,
                    ...(ids.length > 1 ? { ids: ids } : {}),
                },
            }),
            invalidatesTags: [{ type: apiTags.Service, id: "LIST" }],
        }),
        assignServices: builder.mutation<unknown, AssignServicesParams>({
            query: ({ valuationsList, apiParams }) => ({
                resource: "crm-services",
                action: "assign",
                method: "POST",
                data: valuationsList ? { ...apiParams, valuations: valuationsList } : apiParams,
            }),
            invalidatesTags: [{ type: apiTags.Service, id: "LIST" }],
        }),
        getAllServices: builder.query<Service[], { isArchived?: boolean | null }>({
            query: ({ isArchived }) => ({
                resource: "crm-services",
                action: "index",
                method: "GET",
                useAll: true,
                queryBuilder: (query: any) => {
                    query.with("clients", (clientsQuery: any) => {
                        clientsQuery
                            .with("client", (clientQuery: any) => {
                                clientQuery.with("mainContact");
                            })
                            .with("payments", (paymentsQuery: any) => {
                                paymentsQuery.with("tariff");
                            });
                    });
                    if (isArchived !== null && isArchived !== undefined) {
                        query.where("is_archived", isArchived);
                    }
                    return query;
                },
            }),
            providesTags: (result) =>
                result && Array.isArray(result)
                    ? [
                          { type: apiTags.Service, id: "LIST" },
                          ...result.map((item) => ({ type: apiTags.Service, id: item.id })),
                      ]
                    : [{ type: apiTags.Service, id: "LIST" }],
        }),
        getAllServicesCompressed: builder.query<
            unknown,
            { userId: number | string; isArchived?: boolean | null }
        >({
            query: ({ userId, isArchived }) => ({
                resource: "crm-services",
                action: "table-report",
                method: "GET",
                params: {
                    is_archived: isArchived,
                    responsible_id: userId,
                },
            }),
            providesTags: [{ type: apiTags.Service, id: "COMPRESSED" }],
        }),
        createService: builder.mutation<Service, Record<string, unknown>>({
            query: (data) => ({
                resource: "crm-services",
                action: "store",
                method: "POST",
                data,
            }),
            invalidatesTags: [{ type: apiTags.Service, id: "LIST" }],
        }),
        updateService: builder.mutation<Service, { id: number; data: Record<string, unknown> }>({
            query: ({ id, data }) => ({
                resource: "crm-services",
                action: "update",
                method: "PUT",
                id,
                data,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.Service, id: arg.id },
                { type: apiTags.Service, id: "LIST" },
            ],
        }),
        updateServiceArchive: builder.mutation<Service, UpdateServiceArchiveParams>({
            query: ({ id, isArchived }) => ({
                resource: "crm-services",
                action: "update",
                method: "PUT",
                id: id,
                data: { is_archived: isArchived },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.Service, id: arg.id },
                { type: apiTags.Service, id: "LIST" },
            ],
        }),
        calculateSuccessFee: builder.mutation<unknown, CalculateSuccessFeeParams>({
            query: ({ clientId }) => ({
                resource: "crm-services",
                action: "calculate-success",
                method: "POST",
                data: { client_id: clientId },
            }),
            invalidatesTags: [{ type: apiTags.Service, id: "LIST" }],
        }),
        generateServiceExcel: builder.mutation<unknown, GenerateServiceExcelParams>({
            query: ({ data, client, service }) => ({
                resource: "crm-services",
                action: "generate-excel",
                method: "POST",
                data: { data, client, service },
            }),
            invalidatesTags: [{ type: apiTags.Service, id: "LIST" }],
        }),
        getServicesOnConfirmation: builder.query<Service[], ServicesOnConfirmationParams>({
            query: ({ clientId }) => ({
                resource: "crm-services",
                action: "index",
                method: "GET",
                queryBuilder: (query: any) => {
                    return query
                        .with("clients", (clientsQuery: any) => {
                            clientsQuery.orderBy("created_at").where("client_id", "=", clientId);
                        })
                        .whereHas("clients", (clientsQuery: any) => {
                            return clientsQuery.where("client_id", "=", clientId);
                        })
                        .where("is_archived", "=", false);
                },
            }),
            providesTags: (result, error, arg) =>
                result && Array.isArray(result)
                    ? [
                          { type: apiTags.Service, id: `CONFIRMATION_${arg.clientId}` },
                          ...result.map((item) => ({ type: apiTags.Service, id: item.id })),
                      ]
                    : [{ type: apiTags.Service, id: `CONFIRMATION_${arg.clientId}` }],
        }),
    }),
});

export const {
    useGetServicesQuery,
    useUpdateServicePaidMutation,
    useAssignServicesMutation,
    useGetAllServicesQuery,
    useGetAllServicesCompressedQuery,
    useCreateServiceMutation,
    useUpdateServiceMutation,
    useUpdateServiceArchiveMutation,
    useCalculateSuccessFeeMutation,
    useGenerateServiceExcelMutation,
    useGetServicesOnConfirmationQuery,
} = servicesApi;
